import GenericCard from "@ui-components/Card/GenericCard";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import React from "react";
import { PortDetailSection } from "../Port/PortDetails/PortDetailsCard";
import StatusTable from "../Port/PortDetails/StatusTable";
// import { ReactComponent as IconQuestion } from "../../../shared/assets/Icons/Icon_Question.svg";
// import { ReactComponent as IconFolderUp } from "../../../shared/assets/Icons/Icon_FolderUp.svg";
import { useAppSelector } from "@redux/hooks";
import {
  SkeletonLine,
  SkeletonTableRows,
} from "@ui-components/Skeleton/GenericSkeleton";
import { LoadingWrapper } from "../Shared/LoadingWrapper";
import { shapeCarrierAccess } from "kontrolEdge/helpers/carrierAccess";
import {
  buildPhysicalConnection,
  physicalConnectionIds,
} from "kontrolEdge/helpers/physicalConnections";

interface StatusHistoryEntry {
  status: number;
  text: string;
  createdAt: string;
}

interface SubscriptionDetail {
  subscriptionStatus: string;
  subscriptionId: string;
  id: string;
}
interface PortStatus {
  operationalState?: string | null;
  actualState?: string | null;
  lastUpdated?: string | null;
}
interface LatestStatus {
  text?: string | null;
  timestamp?: string | null;
}

interface PortData {
  id: number;
  name: string;
  tag: string;
  portId: string;
  subscriptionId: string;
  subscriptionDetails: SubscriptionDetail;
  statusHistory: StatusHistoryEntry[];
  portStatus?: PortStatus | null;
  latestStatus?: LatestStatus | null;
}

interface ProvisioningDetailsModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
  portDetailsData: PortDetailSection[];
  selectedPort?: SelectedPortSummary | null;
  rawPortData: PortData | null;
}

type TagVariant = "success" | "error" | "warning" | "neutral";

function computeStatusTag(
  opts: {
    operationalState?: string | null;
    detailsStatusText?: string | null;
  } = {}
): { label: string; variant: TagVariant } {
  const raw = (opts.operationalState ?? opts.detailsStatusText ?? "")
    .toString()
    .trim()
    .toLowerCase();

  if (!raw) return { label: "Unknown", variant: "warning" };

  if (["up", "active", "connected"].includes(raw))
    return { label: "Up", variant: "success" };
  if (["down", "inactive", "disconnected"].includes(raw))
    return { label: "Down", variant: "error" };
  if (raw === "unknown") return { label: "Unknown", variant: "warning" };
  if (raw === "warning" || raw === "degraded")
    return { label: "Warning", variant: "warning" };

  return {
    label: raw.charAt(0).toUpperCase() + raw.slice(1),
    variant: "neutral",
  };
}

export interface SelectedPortSummary {
  portId: string;
  customer?: {
    name: string;
  };
}

const ProvisioningDetailsModal: React.FC<ProvisioningDetailsModalProps> = ({
  isOpen,
  onClose,
  portDetailsData,
  rawPortData,
  selectedPort,
  isLoading,
}) => {
  const portDetails = portDetailsData?.find(
    (section) => section.header === "Port Details"
  );

  // Same helper the Port Details card and the ports list use, so all three name
  // the connection identically and carrier access wins over the cross connect in
  // one place only.
  const physicalConnection = buildPhysicalConnection({
    crossConnectId: rawPortData?.crossConnect?.id
      ? rawPortData?.crossConnect?.providerId
      : null,
    crossConnectDetails: rawPortData?.crossConnect?.details ?? null,
    carriers: shapeCarrierAccess(rawPortData),
  });

  const { partnerName, tenantId, partnerId } = useAppSelector(
    (state) => state.selection
  );
  const isViewingAllCustomers = tenantId === partnerId;

  const operationalState =
    rawPortData?.portStatus?.operationalState ??
    (portDetails?.items || []).find(
      (i) => i.label?.toLowerCase() === "operational state"
    )?.value ??
    null;
  const detailsStatusText =
    (portDetails?.items || []).find((i) => i.label === "Status")?.value ??
    rawPortData?.latestStatus?.text ??
    null;

  const { label: statusLabel, variant: statusVariant } = computeStatusTag({
    operationalState: operationalState as string | null,
    detailsStatusText: detailsStatusText as string | null,
  });

  const PortInfoSkeleton: React.FC = () => (
    <div className="space-y-0">
      {/* Service Name */}
      <div className="flex justify-between w-full border-b pb-2">
        <SkeletonLine w="120px" h={16} />
        <SkeletonLine w="40%" h={16} />
      </div>
      {/* Location */}
      <div className="flex justify-between w-full border-b py-2">
        <SkeletonLine w="90px" h={16} />
        <SkeletonLine w="35%" h={16} />
      </div>
      {/* Port Capacity */}
      <div className="flex justify-between w-full border-b py-2">
        <SkeletonLine w="115px" h={16} />
        <SkeletonLine w="30%" h={16} />
      </div>
      {/* Cross Connect */}
      <div className="flex justify-between w-full pt-2">
        <SkeletonLine w="120px" h={16} />
        <SkeletonLine w="30%" h={16} />
      </div>
    </div>
  );

  const PortMetaSkeleton: React.FC<{ showCustomer: boolean }> = ({
    showCustomer,
  }) => (
    <div className="space-y-0">
      {/* Status */}
      <div className="flex items-center pb-2 justify-between w-full border-b">
        <SkeletonLine w="70px" h={16} />
        {/* tag placeholder */}
        <SkeletonLine w="80px" h={20} />
      </div>
      {/* Subscription ID */}
      <div className="flex items-center py-2 justify-between w-full border-b">
        <SkeletonLine w="120px" h={16} />
        <SkeletonLine w="180px" h={16} />
      </div>
      {/* Partner */}
      <div
        className={`flex items-center ${
          showCustomer ? "py-2 border-b" : "pt-2"
        } justify-between w-full`}
      >
        <SkeletonLine w="80px" h={16} />
        <SkeletonLine w="160px" h={16} />
      </div>
      {/* Customer (only if showCustomer) */}
      {showCustomer && (
        <div className="flex items-center pt-2 justify-between w-full">
          <SkeletonLine w="85px" h={16} />
          <SkeletonLine w="180px" h={16} />
        </div>
      )}
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={isLoading ? "Loading…" : "Provisioning Details"}
    >
      <div className="flex-column w-full self-start border-t py-5">
      <div className="flex flex-col md:flex-row px-5 justify-between gap-4">
        {/* <div className="flex px-5 justify-between gap-4"> */}
          <GenericCard
            isCollapsible={false}
            bodyContent={
              <LoadingWrapper
                isLoading={!!isLoading}
                skeleton={<PortInfoSkeleton />}
              >
                {portDetails ? (
                  <>
                    <div className="flex justify-between w-full border-b pb-2">
                      <h3 className="text-sm font-medium text-semantic-text-primary">
                        Service Name
                      </h3>
                      <p className="text-sm font-medium text-semantic-text-secondary">
                        {String(
                          portDetails.items.find(
                            (item) => item.label === "Name"
                          )?.value ?? "N/A"
                        )}
                      </p>
                    </div>

                    <div className="flex justify-between w-full border-b py-2">
                      <h3 className="text-sm font-medium text-semantic-text-primary">
                        Location
                      </h3>
                      <p className="text-sm font-medium text-semantic-text-secondary">
                        {portDetails.items.find(
                          (item) => item.label === "Location"
                        )?.value ?? "N/A"}
                      </p>
                    </div>

                    <div className="flex justify-between w-full border-b py-2">
                      <h3 className="text-sm font-medium text-semantic-text-primary">
                        Port Capacity
                      </h3>
                      <p className="text-sm font-medium text-semantic-text-secondary">
                        {portDetails.items.find(
                          (item) => item.label === "Capacity"
                        )?.value ?? "N/A"}
                      </p>
                    </div>

                    {/*
                      Read from `rawPortData` rather than scraping the rendered
                      section by label: the Port Details card now labels this row
                      by connection type ("Cross Connect" / "Carrier Access"), so
                      a label lookup would silently resolve to N/A.
                    */}
                    <div className="flex justify-between w-full pt-2">
                      <h3 className="text-sm font-medium text-semantic-text-primary">
                        {physicalConnection?.typeLabel ?? "Cross Connect"}
                      </h3>
                      <p className="text-sm font-medium text-semantic-text-secondary">
                        {physicalConnection
                          ? physicalConnectionIds(physicalConnection)
                          : "N/A"}
                      </p>
                    </div>
                  </>
                ) : null}
              </LoadingWrapper>
            }
          />
          <GenericCard
            isCollapsible={false}
            bodyContent={
              <LoadingWrapper
                isLoading={!!isLoading}
                skeleton={
                  <PortMetaSkeleton showCustomer={isViewingAllCustomers} />
                }
              >
                {portDetails ? (
                  <>
                    <div className="flex items-center pb-2 justify-between w-full border-b">
                      <h3 className="text-sm font-medium text-semantic-text-primary">
                        Status
                      </h3>
                      <GenericTag label={statusLabel} variant={statusVariant} />
                    </div>

                    <div className="flex items-center py-2 justify-between w-full border-b">
                      <h3 className="text-sm font-medium text-semantic-text-primary">
                        Subscription ID
                      </h3>
                      <p className="text-sm font-medium text-semantic-text-secondary">
                        {rawPortData?.subscriptionId ??
                          selectedPort?.portId ??
                          "N/A"}
                      </p>
                    </div>

                    {isViewingAllCustomers ? (
                      <>
                        <div className="flex items-center py-2 justify-between w-full border-b">
                          <h3 className="text-sm font-medium text-semantic-text-primary">
                            Partner
                          </h3>
                          <p className="text-sm font-medium text-semantic-text-secondary">
                            {partnerName}
                          </p>
                        </div>
                        <div className="flex items-center pt-2 justify-between w-full">
                          <h3 className="text-sm font-medium text-semantic-text-primary">
                            Customer
                          </h3>
                          <p className="text-sm font-medium text-semantic-text-secondary">
                            {selectedPort?.customer?.name ?? "N/A"}
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center pt-2 justify-between w-full">
                        <h3 className="text-sm font-medium text-semantic-text-primary">
                          Partner
                        </h3>
                        <p className="text-sm font-medium text-semantic-text-secondary">
                          {partnerName}
                        </p>
                      </div>
                    )}
                  </>
                ) : null}
              </LoadingWrapper>
            }
          />
        </div>
        <div className="px-5 py-5">
          <LoadingWrapper
            isLoading={!!isLoading}
            skeleton={<SkeletonTableRows rows={3} cols={3} rowHeight={16} />}
          >
            {rawPortData?.statusHistory?.length ? (
              <StatusTable
                data={rawPortData.statusHistory}
                wrapperClassName="justify-center"
                contentClassName="w-full"
              />
            ) : (
              <p className="w-full text-center">No status history available</p>
            )}
          </LoadingWrapper>
        </div>
        <div className="flex justify-start px-5 py-5 gap-5 border-b">
          {/* <GenericButton
					label="Upload your LOA"
					variant="secondary"
					shape="ghost"
					size="sm"
					icon={<IconFolderUp className="w-6 h-6 text-semantic-text-secondary mr-2"/>}
					// onClick={() => upload LOA functionality}
				/> */}
          {/* <GenericButton
            label="I need help"
            variant="secondary"
            shape="ghost"
            size="sm"
            icon={
              <IconQuestion className="w-5 h-5 text-semantic-text-secondary mr-2" />
            }
            onClick={() => {
              window.Intercom?.("showNewMessage");
            }}
          /> */}
        </div>
      </div>
    </GenericModal>
  );
};

export default ProvisioningDetailsModal;
